import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import LocalStorage from 'services/LocalStorage'

export default function usePersistState<T>(
  initialValue: T,
  persistKey: string,
  json?: boolean
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState(() =>
    LocalStorage.get(persistKey)
      ? json
        ? JSON.parse(LocalStorage.get(persistKey) as string)
        : LocalStorage.get(persistKey)
      : initialValue
  )

  LocalStorage.set(persistKey, json ? JSON.stringify(state) : state)

  return [state, setState]
}

export function useLocalStorageState<T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch {
      return defaultValue
    }
  })

  // Update localStorage when value changes
  const setStoredValue = (newValue: T) => {
    try {
      setValue(newValue)
      localStorage.setItem(key, JSON.stringify(newValue))
      // Dispatch custom event to notify other components
      window.dispatchEvent(
        new CustomEvent('localStorage-change', {
          detail: { key, value: newValue }
        })
      )
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error)
    }
  }

  // Listen for changes from other tabs/windows and custom events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setValue(JSON.parse(e.newValue))
        } catch {
          setValue(e.newValue as T)
        }
      }
    }

    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail.key === key) {
        setValue(e.detail.value)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener(
      'localStorage-change',
      handleCustomStorageChange as EventListener
    )

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(
        'localStorage-change',
        handleCustomStorageChange as EventListener
      )
    }
  }, [key])

  return [value, setStoredValue]
}
